import { useState, type ReactElement } from "react";
import type { UIMessage, DynamicToolUIPart, ToolUIPart, TextUIPart } from "ai";
import { getToolName, isToolUIPart } from "ai";
import { MarkdownRenderer } from "./MarkdownRenderer";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";

type AnyToolPart = DynamicToolUIPart | ToolUIPart;

interface ChatMessageProps {
	message: UIMessage;
	onSaveAsDocument?: (content: string) => void;
}

function ToolInvocationPart({ part }: { part: AnyToolPart }): ReactElement {
	const [expanded, setExpanded] = useState(false);
	const toolLabels: Record<string, string> = {
		search: "Searched library",
		get_docs: "Retrieved document",
		get_project: "Loaded project",
		list_projects: "Listed projects",
		save_doc: "Saved document",
		pull_link: "Submitted URL",
	};

	const name = getToolName(part);
	const label = toolLabels[name] ?? name;
	const isComplete =
		part.state === "output-available" || part.state === "output-error";
	// CONTEXT: DynamicToolUIPart uses "input", ToolUIPart uses "args"
	const input = (("input" in part ? part.input : null) ??
		("args" in part ? part.args : null) ??
		{}) as Record<string, unknown>;
	const argSummary =
		input.q ?? input.title ?? input.project ?? input.url ?? "";

	return (
		<div
			data-testid="tool-invocation"
			className="border-line my-1 rounded border text-xs"
		>
			<button
				type="button"
				onClick={() => setExpanded(!expanded)}
				className="text-fg-secondary hover:text-fg flex w-full items-center gap-1 px-2 py-1"
			>
				{expanded ? (
					<FiChevronDown className="shrink-0" />
				) : (
					<FiChevronRight className="shrink-0" />
				)}
				<span className="font-medium">{label}</span>
				{argSummary && (
					<span className="text-fg-ghost ml-1 truncate">
						{String(argSummary)}
					</span>
				)}
				{!isComplete && (
					<span className="text-accent ml-auto animate-pulse">
						running...
					</span>
				)}
			</button>
			{expanded &&
				isComplete &&
				"output" in part &&
				part.output != null && (
					<pre className="text-fg-ghost border-line max-h-40 overflow-auto border-t px-2 py-1 text-[11px]">
						{JSON.stringify(part.output, null, 2)}
					</pre>
				)}
		</div>
	);
}

// CONTEXT: AI SDK v6 uses "dynamic-tool" for server-defined tools (no client
// tool defs) and "tool-{name}" for client-defined tools. isToolUIPart handles both.
function isToolPart(part: { type: string }): part is AnyToolPart {
	return isToolUIPart(part as any);
}

function isTextPart(part: { type: string }): part is TextUIPart {
	return part.type === "text";
}

// CONTEXT: Parts must be rendered in their natural order from the parts array
// so that text and tool calls appear interleaved as the model produced them.
// Consecutive text parts are grouped together for markdown rendering.
type PartGroup =
	| { kind: "text"; text: string }
	| { kind: "tool"; part: AnyToolPart };

function groupParts(parts: Array<{ type: string } & any>): PartGroup[] {
	const groups: PartGroup[] = [];
	let pendingText = "";

	for (const part of parts) {
		if (isTextPart(part)) {
			pendingText += part.text;
		} else if (isToolPart(part)) {
			if (pendingText) {
				groups.push({ kind: "text", text: pendingText });
				pendingText = "";
			}
			groups.push({ kind: "tool", part });
		}
	}
	if (pendingText) {
		groups.push({ kind: "text", text: pendingText });
	}
	return groups;
}

export function ChatMessage({
	message,
	onSaveAsDocument,
}: ChatMessageProps): ReactElement {
	const isUser = message.role === "user";
	const isAssistant = message.role === "assistant";

	const groups = groupParts(message.parts);
	const fullText = message.parts
		.filter(isTextPart)
		.map((p) => p.text)
		.join("");

	return (
		<div
			data-testid={`chat-message-${message.role}`}
			className={`flex ${isUser ? "justify-end" : "justify-start"}`}
		>
			<div
				className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
					isUser
						? "bg-accent text-surface"
						: "bg-surface-raised text-fg"
				}`}
			>
				{groups.map((group, i) =>
					group.kind === "tool" ? (
						<ToolInvocationPart key={i} part={group.part} />
					) : isAssistant ? (
						<MarkdownRenderer
							key={i}
							markdown={group.text}
							className="chat-message-md text-sm"
						/>
					) : (
						<p key={i} className="whitespace-pre-wrap">
							{group.text}
						</p>
					),
				)}
				{isAssistant && fullText.length > 100 && onSaveAsDocument && (
					<button
						type="button"
						onClick={() => onSaveAsDocument(fullText)}
						className="text-fg-ghost hover:text-accent mt-1 text-[11px] transition-colors"
					>
						Save as document
					</button>
				)}
			</div>
		</div>
	);
}
